import { Link } from 'react-router-dom';
import { CATEGORY_FALLBACK, CATEGORY_PALETTE } from '../../lib/categoryPalette.js';
import {
  categorySpreadEntries,
  formatBriefStat,
} from '../../lib/briefStats.js';

function StatTile({ label, value, sub }) {
  return (
    <div className="border border-[#D4D4D4] bg-[#FAFAFA] px-4 py-5 sm:py-6 min-w-0 min-h-[108px] flex flex-col justify-center">
      <div className="font-mono text-[11px] sm:text-[12px] font-bold tracking-[0.16em] uppercase text-[#737373]">
        {label}
      </div>
      <div className="font-sans text-[clamp(2rem,4.5vw,3.25rem)] font-extrabold text-[#0A0A0A] leading-none mt-2 tabular-nums">
        {value}
      </div>
      {sub ? (
        <div className="text-[12px] sm:text-[13px] text-[#525252] mt-2 leading-snug">{sub}</div>
      ) : null}
    </div>
  );
}

function HomeCategorySpread({ spread, total }) {
  const entries = categorySpreadEntries(spread);
  const totalCount =
    typeof total === 'number' ? total : entries.reduce((sum, [, n]) => sum + n, 0);
  const max = entries.reduce((m, [, n]) => Math.max(m, n), 0);

  if (entries.length === 0) {
    return (
      <p className="text-[13px] text-[#737373] m-0">No categorized stories in this edition yet.</p>
    );
  }

  return (
    <ul className="flex items-end justify-between gap-2 sm:gap-3 m-0 p-0 list-none h-[min(240px,32vh)] min-h-[200px] lg:min-h-[260px]">
      {entries.map(([category, count]) => {
        const palette = CATEGORY_PALETTE[category] || CATEGORY_FALLBACK;
        const heightPct = max > 0 ? Math.max(10, (count / max) * 100) : 0;
        const sharePct = totalCount > 0 ? Math.round((count / totalCount) * 100) : 0;
        return (
          <li
            key={category}
            className="flex-1 flex flex-col items-center gap-2 min-w-0 h-full max-w-[72px] mx-auto"
          >
            <span className="font-mono text-[14px] sm:text-[16px] font-bold tabular-nums text-[#0A0A0A] leading-none">
              {count}
            </span>
            <span className="font-mono text-[9px] sm:text-[10px] font-bold text-[#737373] tabular-nums leading-none">
              {sharePct}%
            </span>
            <div
              className="relative flex-1 w-full min-h-[80px] rounded-[3px] overflow-hidden"
              style={{ backgroundColor: palette.track }}
              role="img"
              aria-label={`${category}: ${count} articles, ${sharePct}% of brief`}
            >
              <div
                className="absolute bottom-0 left-0 right-0 rounded-[3px] transition-[height] duration-300 ease-out"
                style={{ height: `${heightPct}%`, backgroundColor: palette.bar }}
              />
            </div>
            <span className="font-mono text-[9px] sm:text-[10px] font-bold tracking-[0.1em] uppercase text-[#0A0A0A] text-center leading-tight px-0.5">
              {category}
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default function HomeFeaturedArticle({ model, status, error }) {
  if (status === 'loading') {
    return (
      <section className="max-w-page mx-auto px-4 sm:px-6 py-10">
        <p className="text-[13px] text-hxd-muted italic text-center m-0">Loading today&apos;s brief…</p>
      </section>
    );
  }

  if (status === 'error') {
    return (
      <section className="max-w-page mx-auto px-4 sm:px-6 py-10">
        <p className="text-[13px] text-red-600 text-center m-0">Couldn&apos;t load today&apos;s brief: {error}</p>
      </section>
    );
  }

  if (!model) {
    return (
      <section className="max-w-page mx-auto px-4 sm:px-6 py-10">
        <h2 className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange m-0 mb-2">
          Today&apos;s brief
        </h2>
        <p className="text-[14px] text-hxd-muted m-0">
          No brief published yet.{' '}
          <Link to="/archive" className="text-hxd-orange font-semibold hover:underline">
            Browse the archive
          </Link>
          .
        </p>
      </section>
    );
  }

  const article = model.articles?.[0];
  const imageUrl = article?.imageUrl || model.heroImage?.url;
  const imageAlt = article?.imageAlt || model.heroImage?.alt || '';
  const headline = article?.title;
  const snippet = article?.takeaway || article?.summary;
  const briefLink = article?.articleId
    ? `/today#article-${article.articleId}`
    : '/today';

  const stats = model.briefStats;
  const inBrief = stats?.inBrief ?? model.articles?.length ?? 0;
  const reviewed = stats?.reviewed ?? null;
  const filteredOut = stats?.filteredOut ?? null;
  const storyDupesMerged = stats?.storyDupesMerged ?? null;
  const categorySpread = stats?.categorySpread || {};

  if (!article) {
    return (
      <section className="max-w-page mx-auto px-4 sm:px-6 py-10 sm:py-12">
        <div className="border border-black bg-white p-5 sm:p-6">
          <p className="text-[14px] text-hxd-muted m-0">
            No stories in today&apos;s brief yet.{' '}
            <Link to="/archive" className="text-hxd-orange font-semibold hover:underline">
              Browse the archive
            </Link>
            .
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="max-w-page mx-auto px-4 sm:px-6 py-10 sm:py-12" aria-labelledby="home-lead-story-heading">
      <div className="border border-black bg-white p-5 sm:p-6 lg:p-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-10">
          <div className="min-w-0 flex flex-col order-2 lg:order-1 gap-6 lg:min-h-[520px] lg:justify-between">
            <div className="grid grid-cols-2 gap-3 sm:gap-4">
              {reviewed != null ? (
                <StatTile
                  label="Articles scanned"
                  value={formatBriefStat(reviewed)}
                  sub="Across overnight sources"
                />
              ) : null}
              <StatTile
                label="Worth your time"
                value={formatBriefStat(inBrief)}
                sub={`In today’s ${inBrief === 1 ? 'story' : 'stories'}`}
              />
              {filteredOut != null ? (
                <StatTile
                  label="Filtered out"
                  value={formatBriefStat(filteredOut)}
                  sub="Noise & off-brief"
                />
              ) : null}
              {storyDupesMerged != null ? (
                <StatTile
                  label="Stories merged"
                  value={formatBriefStat(storyDupesMerged)}
                  sub="Same story, different outlet"
                />
              ) : null}
            </div>

            <div className="flex flex-col flex-1 min-h-[240px]">
              <div className="flex items-baseline justify-between gap-3 mb-4 pb-2 border-b-2 border-hxd-orange shrink-0">
                <h3 className="font-display text-[11px] font-bold tracking-display-md uppercase text-[#0A0A0A] m-0">
                  Category spread
                </h3>
                <span className="font-mono text-[11px] font-bold text-[#737373] tabular-nums">
                  {inBrief} total
                </span>
              </div>
              <div className="flex-1 flex flex-col justify-end">
                <HomeCategorySpread spread={categorySpread} total={inBrief} />
              </div>
            </div>
          </div>

          <div className="min-w-0 flex flex-col order-1 lg:order-2">
            <p className="font-display text-[11px] font-bold tracking-display-md uppercase text-hxd-orange m-0 mb-3">
              Today&apos;s story
              {model.briefDate ? (
                <span className="text-[#737373] font-mono normal-case tracking-[0.08em] font-bold">
                  {' '}
                  · {model.briefDate}
                </span>
              ) : null}
            </p>
            <h2
              id="home-lead-story-heading"
              className="font-sans text-[clamp(1.25rem,2.5vw,1.75rem)] font-extrabold leading-tight text-hxd-orange m-0"
            >
              {headline}
            </h2>
            {snippet ? (
              <p className="mt-4 text-[15px] sm:text-[16px] leading-relaxed text-[#525252] m-0 line-clamp-5">
                {snippet}
              </p>
            ) : null}
            <Link to={briefLink} className="hxd-btn hxd-btn-primary mt-6 inline-block text-[13px] self-start">
              Read the brief →
            </Link>
            <div className="mt-6 min-h-[200px] sm:min-h-[280px] bg-hxd-surface border border-[#D4D4D4] overflow-hidden flex items-center justify-center">
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={imageAlt}
                  className="w-full h-full max-h-[360px] object-cover"
                  loading="lazy"
                />
              ) : (
                <img
                  src="/images/hxd-logo-header.png"
                  alt="Hangar X Daily — Morning Brief"
                  className="block h-8 w-auto opacity-40"
                  loading="lazy"
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
